/* eslint-disable @typescript-eslint/naming-convention */
import { preventOneWordInLastLine } from '@lemonade-hq/cdk';
import type { WizardComponentAdapterProps } from '@lemonade-hq/chat-wizard-client-base';
import {
  DIAMOND_SPECIAL_CHAR,
  isIconProps,
  isNewLineProps,
  isTagProps,
  isTextProps,
} from '@lemonade-hq/uniclient-chat';
import type { FormattedText } from '@lemonade-hq/uniclient-chat';
import React, { lazy, Suspense } from 'react';
import type { FormattedTextItemType } from '../elements/WizardFormattedText';
import { WizardFormattedText } from '../elements/WizardFormattedText';
import * as styles from '../elements/WizardTitle.css';

const Diamond = lazy(
  async () => await import('../adapters/components/Diamond/Diamond').then(m => ({ default: m.Diamond })),
);

const PLACEHOLDERS: Record<string, React.ReactElement> = {
  [DIAMOND_SPECIAL_CHAR]: (
    <span className={styles.diamondSpan} data-testid="diamond-icon">
      <Suspense fallback={null}>
        <Diamond height="24px" width="24px" />
      </Suspense>
    </span>
  ),
};

type FormattedTypeItemNode = FormattedTextItemType | FormattedTextItemType[];

/**
 * Processes a text string by replacing special characters with their corresponding React elements
 * and applying one word in last line prevention to ensure proper text wrapping
 * @param text - The text string to process
 * @returns An array of React nodes with special characters replaced by component placeholders
 */
const processText = (text: string, isLastNode?: boolean): React.ReactNode[] => {
  const allPlaceholderChars = Object.keys(PLACEHOLDERS).filter(k => text.includes(k));

  if (allPlaceholderChars.length === 0) {
    return isLastNode ? [preventOneWordInLastLine(text, false, false)] : [text];
  }

  return allPlaceholderChars.reduce<React.ReactNode[]>(
    (acc, char) => {
      return acc.flatMap(node => {
        /* istanbul ignore if -- @preserve - can't mock React.ReactNode here */
        if (typeof node !== 'string') {
          return node;
        }

        const parts = node.split(char);
        return parts.flatMap((part, index, array) => {
          const nodes: React.ReactNode[] = part ? [part] : [];
          return index < array.length - 1 ? [...nodes, PLACEHOLDERS[char]] : nodes;
        });
      });
    },
    [text],
  );
};

/**
 * Processes a formatted node and converts it to a React node
 * @param item - The formatted item to process
 * @param onAction - Optional callback for handling actions
 * @returns A React node representing the formatted item
 */
const processFormattedNode = (
  item: FormattedTextItemType,
  onAction?: WizardComponentAdapterProps['onAction'],
  isLastNode?: boolean,
): React.ReactNode => {
  const isPlainText = typeof item === 'object' && Object.keys(item).length === 1 && 'text' in item;
  // TODO: find a better solution
  const key = JSON.stringify(item);
  if (isPlainText) {
    return processText(item.text, isLastNode);
  } else {
    return (
      <WizardFormattedText
        isLastNode={isLastNode}
        key={key}
        onAction={onAction}
        text={item}
        wrapperClassName={styles.formattedTextWrapper}
      />
    );
  }
};

/**
 * Processes a formatted node or array of nodes and converts them to React nodes
 * @param node - The node or array of nodes to process
 * @param onAction - Optional callback for handling actions
 * @returns An array of React nodes
 */
const processNode = (
  node: FormattedTypeItemNode,
  onAction?: WizardComponentAdapterProps['onAction'],
  isLastNode = true,
): React.ReactNode[] => {
  if (React.isValidElement(node)) return [node];
  if (typeof node === 'string') return processText(node, isLastNode);
  if (isNewLineProps(node)) return [processFormattedNode({ text: '\n' }, onAction, isLastNode)];
  if (Array.isArray(node)) return node.flatMap((item, index) => processNode(item, onAction, index === node.length - 1));

  return [processFormattedNode(node, onAction, isLastNode)];
};

/**
 * Converts formatted text to a plain string
 * @param text - The formatted text to convert
 * @returns A string representation of the formatted text
 */
export const formattedTextToString = (text: FormattedTypeItemNode): string => {
  if (typeof text === 'string') return text;
  if (isIconProps(text)) return '';
  if (isTextProps(text)) return text.text;
  if (isNewLineProps(text)) return '\n';
  if (isTagProps(text)) return text.content;
  if (Array.isArray(text)) return text.map(formattedTextToString).join('');

  return '';
};

/**
 * Extracts a key string from formatted text
 * @param text - The formatted text to extract a key from
 * @returns A string key derived from the formatted text
 */
export const getFormattedTextKey = (text: FormattedText): string => {
  if (typeof text === 'string') {
    return text;
  } else if (Array.isArray(text)) {
    return text.map(item => getFormattedTextKey(item as FormattedText)).join('');
  } else {
    const isActionItem = 'type' in text && text.type === 'action';
    if ('text' in text && !isActionItem) {
      return text.text;
    } else {
      return '';
    }
  }
};

/**
 * Parses a formatted title into React nodes and a stripped text string
 * @param title - The formatted title to parse
 * @param onAction - Optional callback for handling actions
 * @returns Object containing parsed React nodes and a plain text version with special characters removed
 */
export function parseFormattedTitle(
  title: FormattedText,
  onAction?: WizardComponentAdapterProps['onAction'],
): {
  readonly parsedNodes: React.ReactNode[];
  readonly strippedText: string;
} {
  const titleString = formattedTextToString(title);
  const strippedText = Object.keys(PLACEHOLDERS).reduce(
    (acc, placeholder) => acc.replaceAll(placeholder, ''),
    titleString,
  );
  const parsedNodes = processNode(title, onAction);

  return { parsedNodes, strippedText };
}
